import { GeoIntersection, GeometryBase, Line, Point } from "tnbase";
import { EventHandlerBase, Primitive, PrimitiveType, RenderItemType } from "pytha2";

/**
 * 对齐轴网处理器
 */
export class EventHandlerAlignAxisNet extends EventHandlerBase {
    run(data?: {}): boolean {
        /**
         * 对齐轴网步骤：
         * 1. 获取所有轴网图元
         * 2. 拆分获得几何层轴线
         * 3. 遍历轴线列表，获取两两之间的交点，以及线的交点关系map
         * 4. 对于每条线：
         *      a. 获取线的交点列表，根据交点列表对线进行剪裁
         *      b. 若是线的交点小于等于1，保留改线
         *      c. 若是线的交点大于1，则该线只保留最两边的交点之间的部分
         * 5. 删除已经生成的标注与轴号
         */
        const storage = this.getPythaStorage();
        const primitiveList: Primitive[] = Object.keys(storage.primitiveStore.primitiveDict)?.map(key => storage.primitiveStore.primitiveDict[key]) || [];
        // 找到所有的轴线（type为AxisLine的）
        const axisLinePriList = primitiveList?.filter(item => item.type === "AxisLine") || [];
        // 轴网图元与几何的对应map
        let linePriGeoMap: { [key: string]: number[] } = {}
        // 获取轴线的geo
        let geoLineList: Line[] = [];
        axisLinePriList.forEach(linePri => {
            linePriGeoMap[linePri.uuid] = []
            linePri.geos.forEach(geo => {
                if (geo.type === PrimitiveType.Line) {
                    let geoClone = geo.clone() as Line
                    if (linePri.worldModelMatrix) {
                        const geoStart = geoClone.start.applyMatrix4(linePri.worldModelMatrix);
                        const geoEnd = geoClone.end.applyMatrix4(linePri.worldModelMatrix);
                        geoClone.start = geoStart;
                        geoClone.end = geoEnd;
                    }
                    geoLineList.push(geoClone);
                    linePriGeoMap[linePri.uuid].push(geoLineList.length - 1)
                }
            })
        })
        // 线的交点关系map
        let lineIntersectPointMap: { [key: number]: number[] } = {}
        // 交点集合
        let intersections = [];
        // 两两线求交
        for (let i = 0; i < geoLineList.length; i++) {
            const geoLine1 = geoLineList[i]
            if (!lineIntersectPointMap[i]) {
                lineIntersectPointMap[i] = []
            }
            for (let j = i + 1; j < geoLineList.length; j++) {
                if (!lineIntersectPointMap[j]) {
                    lineIntersectPointMap[j] = []
                }
                const geoLine2 = geoLineList[j]
                // 计算两条线的交点
                const intersection = GeoIntersection.LineIntersectSegment(geoLine1, geoLine2.start, geoLine2.end, true, true);
                if (!!intersection) {
                    let pointIndex = this.pushPointIntoArray(intersection, intersections);
                    lineIntersectPointMap[i].push(pointIndex)
                    lineIntersectPointMap[j].push(pointIndex)
                }
            }
        }

        for (let i = 0; i < geoLineList.length; i++) {
            this.trimLineByInterections(geoLineList[i], intersections, lineIntersectPointMap[i])
        }
        // 更新网信息
        this.node.graph.cache.primitiveList = []
        for (let i = 0; i < axisLinePriList.length; i++) {
            let clonePri = new Primitive("AxisLine")
            clonePri.uuid = axisLinePriList[i].uuid
            clonePri.needRedraw = true
            let geos = this.findGeosInArrayByIndex(geoLineList, linePriGeoMap[clonePri.uuid])
            this.addPrimitive(clonePri, geos)
            this.node.graph.cache.primitiveList.push(clonePri)
        }
        // 若是有轴号或标注，则需要删除
        // 文字
        const textPrimitives = primitiveList.filter(item => item.type == "Text") || []
        // 轴号图元
        const axisMarkLine = primitiveList.filter(item => item.type == "AxisMarkLine") || []
        // 标注图元
        const markLine = primitiveList.filter(item => item.type == "MarkLine") || []
        // 组合
        const deletePris = [...textPrimitives, ...axisMarkLine, ...markLine]
        this.node.graph.cache.deletePrimitiveList = deletePris

        return true
    }

    findGeosInArrayByIndex(geos: Line[], indexList: number[]) {
        let res: Line[] = []

        for (let i = 0; i < geos.length; i++) {
            if (indexList.includes(i)) {
                res.push(geos[i])
            }
        }

        return res

    }


    /**
     * 将点 point 添加到数组 array 内，返回点在数组中的索引
     * @param point 
     * @param array 
     * @returns 
     */
    pushPointIntoArray(point: Point, array: Point[]) {
        let index = array.findIndex(item => item.equals(point))
        if (index < 0) {
            array.push(point)
            index = array.length - 1
        }
        return index
    }

    /**
     * 将线根据交点集合切割
     * @param line 
     * @param intersections 
     * @param indexList 
     */
    trimLineByInterections(line: Line, intersections: Point[], indexList: number[]) {
        if (indexList.length < 1) {
            return line
        }
        // 获取线的起点，获取距离起点最近与最远的点
        let startPoint = line.start
        let min = Number.MAX_VALUE
        let max = Number.MIN_VALUE
        let minIndex = 0
        let maxIndex = 0
        for (let i = 0; i < indexList.length; i++) {
            let intersectPoint = intersections[indexList[i]]
            let distance = startPoint.distanceTo(intersectPoint)

            if (distance > max) {
                max = distance
                maxIndex = indexList[i]
            }
            if (distance < min) {
                min = distance
                minIndex = indexList[i]
            }
        }
        if (minIndex != maxIndex) {
            line.set(intersections[minIndex], intersections[maxIndex])
        }
        return line

    }

    /**
     * 添加一个图元
     * @param primitive 
     * @param geos  
     */
    addPrimitive(primitive: Primitive, geos: GeometryBase[]) {
        const storage = this.getPythaStorage();
        primitive.geos.push(...geos);
        let keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = [];
            renderItem.mat.color.set(0xE54242);
            geos.forEach(geo => {
                let geoElementsLength = renderItem.geoElements.length;
                geo.getGeoElementsIdxList().forEach(idx => {
                    renderItem.geoElements.push(idx + geoElementsLength);
                })
            });
            renderItem.renderType = RenderItemType.LINE;
        }
    }
}